import React from 'react';
import { Dropdown, Form, Image, Select, Row, Col } from 'antd';
import AppModal from '@/pages/inside/tenant/AppModal';
import { GlobalImagePreviewStyles } from '@/components/ui/GlobalImagePreviewStyles';
import { Container } from '@/components/ui/Container';
import ClampedMarkdownDescription from '@/components/ui/ClampedMarkdownDescription';
import RichTextEditor from '@/components/ui/RichTextEditor';
import { Markdown } from '@/components/ui/Markdown';
import {
    FileOutlined,
    LinkOutlined,
    PictureOutlined,
    MessageOutlined,
    BugOutlined,
    PaperClipOutlined,
    EditOutlined,
    DeleteOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import PhaseDescriptionAttachments from '@/components/module/projects/milestone_tab/PhaseDescriptionAttachments';
import ProjectBugCard from '@/components/module/projects/shared/ProjectBugCard';
import { Button } from '@/components/ui/Button';
import TaskBlockerCreateModal from '@/components/module/projects/shared/TaskBlockerCreateModal';
import BugDiscussionSidePanel from '@/components/module/projects/discussion/BugDiscussionSidePanel';
import BlockerDiscussionSidePanel from '@/components/module/projects/discussion/BlockerDiscussionSidePanel';
import { BacklogTaskBanner } from '@/components/sprint/BacklogTaskBanner';
import MilestoneLinkModal from '@/components/module/projects/milestone_tab/MilestoneLinkModal';
import { PendingAttachmentTile, PendingLinkWrap } from '@/components/module/projects/sprints_tab/PendingRichTextAttachments';
import {
    AttachmentCardsRow,
    CreateRoleModalBody,
    ModalChromeHeading,
    ModalBlockLabel,
    PanelSectionLabel,
    MutedText,
    CardDescriptionInner,
    ActionsWrap,
    SectionBadgeBug,
    BoardUnderlineTabRow,
    BoardUnderlineTabInner,
    BoardUnderlineTab,
    BoardUnderlineTabLine,
    BoardUnderlineTabPrimaryLabel,
    BoardUnderlineTabCountBadge,
    EmptyStateBlock,
    EmptyStateIcon,
    EmptyStateTitle,
    EmptyStateDescription,
    ContentCard,
    TimelineWrap,
    TimelineLine,
    TimelineItem,
    TimelineItemContent,
    TimelineIconWrap,
    TimelineIcon,
    TimelineDate,
    TimelineCard,
    TimelineCardTitle,
    TimelineCardMeta,
    AvatarStackCircle,
    AvatarStackContainer,
} from '@/pages/inside/tenant/style';
import { StopOutlined } from '@ant-design/icons';
import TaskAttachmentTile from '@/components/module/projects/task/TaskAttachmentTile';
import StatusBadge from '@/components/ui/StatusBadge';
import { useTaskDetailModal } from '@hooks/projects/task/useTaskDetailModal';
import UserAvatar from '@/components/ui/UserAvatar';
import { UserAvatarWithRoleTooltip } from '@/components/ui/UserAvatarWithRoleTooltip';
import { IconChipButton } from '@/components/module/projects/task/TaskBlockerCard.parts';
import { truncateFileName } from '@hooks/projects/projectHelpers';
import {
    projectSprintTaskModalFormStyles,
    submissionRichDescriptionFormItemStyle,
    submissionRichDescriptionLabelStyle,
} from '@/pages/inside/tenant/project/projectTabStyles';

function SubmissionAttachmentsDropdown({ vm, submission }) {
    const links = submission?.linksPresentation ?? [];
    const files = submission?.filesPresentation ?? [];
    const hasAny = (links?.length ?? 0) + (files?.length ?? 0) > 0;

    const chip = (
        <Container
            $display="inline-flex"
            $alignItems="center"
            $gap="6px"
            style={{
                padding: '4px 10px',
                borderRadius: 999,
                border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                fontSize: 12,
                fontWeight: 600,
                cursor: hasAny ? 'pointer' : 'not-allowed',
                userSelect: 'none',
            }}
            onClick={(e) => {
                // prevent “bounce”/active press behavior from nested clickable rows
                e.preventDefault();
                e.stopPropagation();
            }}
        >
            <PaperClipOutlined style={{ fontSize: 12 }} />
            <span>Attachments</span>
            <span>{submission?.attachmentCount ?? 0}</span>
        </Container>
    );

    if (!hasAny) return chip;

    return (
        <Dropdown
            trigger={['click']}
            dropdownRender={() => (
                <div
                    style={{
                        width: 420,
                        maxWidth: 'calc(100vw - 60px)',
                        maxHeight: 320,
                        overflow: 'auto',
                        padding: 12,
                        borderRadius: 12,
                        background: 'var(--ant-color-bg-elevated, #fff)',
                        border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                        boxShadow: '0 10px 28px rgba(0,0,0,0.12)',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {links.length > 0 ? (
                        <Container $display="flex" $flexDirection="column" $gap="8px" style={{ marginBottom: files.length ? 12 : 0 }}>
                            <PanelSectionLabel style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 0, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.75 }}>
                                <LinkOutlined />
                                Links
                            </PanelSectionLabel>
                            <Container $display="flex" $flexWrap="wrap" $gap="8px">
                                {links.map((item, idx) => (
                                    <a
                                        key={`${item?.href ?? item?.url ?? 'link'}-${idx}`}
                                        href={item?.href || '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {item?.title || 'Link'}
                                    </a>
                                ))}
                            </Container>
                        </Container>
                    ) : null}

                    {files.length > 0 ? (
                        <Container $display="flex" $flexDirection="column" $gap="8px">
                            <PanelSectionLabel style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 0, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.75 }}>
                                <FileOutlined />
                                Attachments
                            </PanelSectionLabel>
                            <AttachmentCardsRow>
                                {files.map((item, idx) => (
                                    <TaskAttachmentTile
                                        key={`${item?.href ?? item?.url ?? 'file'}-${idx}`}
                                        href={item?.href}
                                        title={item?.title}
                                        isImage={item?.isImage}
                                        ext={item?.ext}
                                        onPreviewImage={(url) => vm.setDescriptionPreviewImage(url)}
                                        onOpenFile={(url) => window.open(url, '_blank', 'noopener,noreferrer')}
                                    />
                                ))}
                            </AttachmentCardsRow>
                        </Container>
                    ) : null}
                </div>
            )}
        >
            {chip}
        </Dropdown>
    );
}

export default function TaskDetailModal({
    open,
    onClose,
    title,
    footer = null,
    projectId,
    task,
    taskDescriptionMarkdown,
    taskBugs = [],
    mentionUsers = [],
    mentionMilestones = [],
    mentionSprints = [],
    onRequestRefresh,
    hideBugsSection = false,
    readOnlyBugActions = true,
    allowAddBlocker = true,
    bugsPanelExtra = null,
    showBugForm,
    openBugForm,
    closeBugForm,
    editingPendingBugId,
    pendingBugs,
    removePendingBug,
    startEditPendingBug,
    bugForm,
    saveBugDraft,
    submitAllBugs,
    createBugPending,
    bugPendingAttachments,
    bugPendingLinks,
    handleBugFilesSelected,
    removeBugAttachment,
    bugLinkModalOpen,
    bugLinkForm,
    handleBugLinkRequest,
    closeBugLinkModal,
    handleBugLinkModalSubmit,
    removeBugPendingLink,
    handleBugEditorLinkRemoved,
}) {
    const vm = useTaskDetailModal({
        open,
        projectId,
        task,
        taskDescriptionMarkdown,
        taskBugs,
        onRequestRefresh,
    });

    const computedTitle = title ?? (
        open && task ? (
            <Container $display="flex" $flexDirection="column" $gap="4px">
                <MutedText style={{ fontSize: 12, margin: 0 }}>{vm.taskDisplayId}</MutedText>
                <ModalChromeHeading style={{ fontSize: 20 }}>{vm.taskTitle}</ModalChromeHeading>
                <Container $display="flex" $alignItems="center" $gap="12px" style={{ marginTop: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--ant-color-text-secondary, #6b7280)' }}>
                        People:
                    </span>
                    <AvatarStackContainer>
                        {vm.createdByPresentation?.name && (
                            <span className="avatar-member">
                                <UserAvatar
                                    name={vm.createdByPresentation.name}
                                    role={vm.createdAtLabel ? `Creator — Created ${vm.createdAtLabel}` : 'Creator'}
                                    imageUrl={vm.createdByPresentation.imageUrl}
                                    initials={vm.createdByPresentation.initials}
                                    avatarBg={vm.createdByPresentation.color}
                                    size={36}
                                    stacked={true}
                                />
                            </span>
                        )}
                        {vm.assigneePresentation?.name && (
                            <span className="avatar-member">
                                <UserAvatar
                                    name={vm.assigneePresentation.name}
                                    role={vm.assignedAtLabel ? `Assignee — Assigned ${vm.assignedAtLabel}` : 'Assignee'}
                                    imageUrl={vm.assigneePresentation.imageUrl}
                                    initials={vm.assigneePresentation.initials}
                                    avatarBg={vm.assigneePresentation.color}
                                    size={36}
                                    stacked={true}
                                />
                            </span>
                        )}
                    </AvatarStackContainer>
                </Container>
                {vm.isDevBacklog ? (
                    <div style={{ marginTop: 4 }}>
                        <BacklogTaskBanner />
                    </div>
                ) : null}
            </Container>
        ) : null
    );

    return (
        <>
            <GlobalImagePreviewStyles />
            <AppModal size="lg" lgWidth="min(1180px, calc(100vw - 48px))" open={open} onCancel={onClose} title={computedTitle} footer={footer}>
                {open && task ? (
                    <CreateRoleModalBody
                        $flush
                        style={{
                            height: '70vh',
                            maxHeight: '70vh',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Container $display="flex" $flexDirection="column" $gap="16px" style={{ flex: 1, minHeight: 0 }}>
                            <div>
                                <Container $display="flex" $alignItems="center" $justifyContent="space-between" $gap="12px">
                                    <ModalBlockLabel as="div" style={{ display: 'block', marginBottom: 8 }}>
                                        Task Description
                                    </ModalBlockLabel>
                                    <span
                                        style={{
                                            marginBottom: 8,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            padding: '4px 10px',
                                            borderRadius: 999,
                                            fontSize: 12,
                                            fontWeight: 600,
                                            border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                                            background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                                        }}
                                    >
                                        {vm.taskTypeLabel}
                                    </span>
                                </Container>
                                {!vm.canViewTaskDescription ? (
                                    <MutedText style={{ margin: 0 }}>You don’t have permission to view the description.</MutedText>
                                ) : vm.hasDescription ? (
                                    <div style={{ maxHeight: 220, overflowY: 'auto', paddingRight: 4 }}>
                                        <Markdown
                                            source={vm.resolvedTaskDescriptionMarkdown}
                                            component={CardDescriptionInner}
                                            richInlineAnchors
                                            linkPrimary
                                            attachmentItems={vm.descriptionAttachmentItems}
                                            onLinkClick={(payload) => vm.handleMarkdownImageLinkClick(payload, vm.descriptionAttachmentItems)}
                                            emptyFallback="—"
                                        />
                                    </div>
                                ) : (
                                    <MutedText style={{ margin: 0 }}>No description</MutedText>
                                )}
                            </div>

                            <BoardUnderlineTabRow style={{ marginTop: 4, marginBottom: 0 }}>
                                <BoardUnderlineTabInner style={{ paddingLeft: 0, paddingRight: 0 }}>
                                    <BoardUnderlineTab type="button" $active={vm.detailTab === 'attachments'} onClick={() => vm.setDetailTab('attachments')}>
                                        <BoardUnderlineTabLine>
                                            <BoardUnderlineTabPrimaryLabel $active={vm.detailTab === 'attachments'} style={{ fontSize: 12 }}>
                                                Attachments
                                            </BoardUnderlineTabPrimaryLabel>
                                            <BoardUnderlineTabCountBadge $active={vm.detailTab === 'attachments'}>
                                                {vm.attachmentCount}
                                            </BoardUnderlineTabCountBadge>
                                        </BoardUnderlineTabLine>
                                    </BoardUnderlineTab>
                                    {!hideBugsSection ? (
                                        <BoardUnderlineTab type="button" $active={vm.detailTab === 'bugs'} onClick={() => vm.setDetailTab('bugs')}>
                                            <BoardUnderlineTabLine>
                                                <BoardUnderlineTabPrimaryLabel $active={vm.detailTab === 'bugs'} style={{ fontSize: 12 }}>
                                                    Bugs &amp; Reports
                                                </BoardUnderlineTabPrimaryLabel>
                                                <BoardUnderlineTabCountBadge $active={vm.detailTab === 'bugs'}>
                                                    {vm.bugCount}
                                                </BoardUnderlineTabCountBadge>
                                            </BoardUnderlineTabLine>
                                        </BoardUnderlineTab>
                                    ) : null}
                                    <BoardUnderlineTab type="button" $active={vm.detailTab === 'blockers'} onClick={() => vm.setDetailTab('blockers')}>
                                        <BoardUnderlineTabLine>
                                            <BoardUnderlineTabPrimaryLabel $active={vm.detailTab === 'blockers'} style={{ fontSize: 12 }}>
                                                Blockers
                                            </BoardUnderlineTabPrimaryLabel>
                                            <BoardUnderlineTabCountBadge $active={vm.detailTab === 'blockers'}>
                                                {vm.blockerCount}
                                            </BoardUnderlineTabCountBadge>
                                        </BoardUnderlineTabLine>
                                    </BoardUnderlineTab>
                                    {vm.qaSubmission ? (
                                        <BoardUnderlineTab type="button" $active={vm.detailTab === 'qa'} onClick={() => vm.setDetailTab('qa')}>
                                            <BoardUnderlineTabLine>
                                                <BoardUnderlineTabPrimaryLabel $active={vm.detailTab === 'qa'} style={{ fontSize: 12 }}>
                                                    Task submission
                                                </BoardUnderlineTabPrimaryLabel>
                                                <BoardUnderlineTabCountBadge $active={vm.detailTab === 'qa'}>
                                                    {vm.qaSubmission?.attachmentCount ?? 0}
                                                </BoardUnderlineTabCountBadge>
                                            </BoardUnderlineTabLine>
                                        </BoardUnderlineTab>
                                    ) : null}
                                    {vm.consultationData ? (
                                        <BoardUnderlineTab type="button" $active={vm.detailTab === 'consultation'} onClick={() => vm.setDetailTab('consultation')}>
                                            <BoardUnderlineTabLine>
                                                <BoardUnderlineTabPrimaryLabel $active={vm.detailTab === 'consultation'} style={{ fontSize: 12 }}>
                                                    Consultation
                                                </BoardUnderlineTabPrimaryLabel>
                                                <BoardUnderlineTabCountBadge $active={vm.detailTab === 'consultation'}>
                                                    {vm.consultationData.attachmentCount}
                                                </BoardUnderlineTabCountBadge>
                                            </BoardUnderlineTabLine>
                                        </BoardUnderlineTab>
                                    ) : null}
                                    {vm.showActivityTab ? (
                                        <BoardUnderlineTab type="button" $active={vm.detailTab === 'activity'} onClick={() => vm.setDetailTab('activity')}>
                                            <BoardUnderlineTabLine>
                                                <BoardUnderlineTabPrimaryLabel $active={vm.detailTab === 'activity'} style={{ fontSize: 12 }}>
                                                    Activity log
                                                </BoardUnderlineTabPrimaryLabel>
                                                <BoardUnderlineTabCountBadge $active={vm.detailTab === 'activity'}>
                                                    {vm.activityCount}
                                                </BoardUnderlineTabCountBadge>
                                            </BoardUnderlineTabLine>
                                        </BoardUnderlineTab>
                                    ) : null}
                                </BoardUnderlineTabInner>
                            </BoardUnderlineTabRow>

                            <div style={{ paddingRight: 4, flex: 1, minHeight: 0, overflowY: 'auto' }}>
                                {vm.detailTab === 'attachments' && (
                                    <Container $display="flex" $flexDirection="column" $gap="10px" style={{ minHeight: '100%' }}>
                                        {vm.attachmentCount === 0 ? (
                                            <EmptyStateBlock style={{ minHeight: '100%', padding: '36px 24px' }}>
                                                <EmptyStateIcon>
                                                    <FileOutlined />
                                                </EmptyStateIcon>
                                                <EmptyStateTitle>No attachments</EmptyStateTitle>
                                                <EmptyStateDescription>Add files or links to see them here.</EmptyStateDescription>
                                            </EmptyStateBlock>
                                        ) : (
                                            <>
                                                {vm.attachmentLinks.length > 0 ? (
                                                    <Container $display="flex" $flexDirection="column" $gap="8px">
                                                        <PanelSectionLabel style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 0, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.75 }}>
                                                            <LinkOutlined />
                                                            Links
                                                        </PanelSectionLabel>
                                                        <Container $display="flex" $flexWrap="wrap" $gap="8px">
                                                            {vm.attachmentLinks.map((item, idx) => {
                                                                const href = vm.normalizePhaseLinkUrl(item.rawUrl);
                                                                const label = (item.name || href || 'Link').trim();
                                                                return (
                                                                    <a key={`${item.id}-${idx}`} href={href || '#'} target="_blank" rel="noopener noreferrer">
                                                                        {label}
                                                                    </a>
                                                                );
                                                            })}
                                                        </Container>
                                                    </Container>
                                                ) : null}

                                                {vm.attachmentFilesPresentation.length > 0 ? (
                                                    <Container $display="flex" $flexDirection="column" $gap="8px">
                                                        <PanelSectionLabel style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 0, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.75 }}>
                                                            <FileOutlined />
                                                            Attachments
                                                        </PanelSectionLabel>
                                                        <AttachmentCardsRow>
                                                            {vm.attachmentFilesPresentation.map(({ item, href, isImage, ext }, idx) => (
                                                                <TaskAttachmentTile
                                                                    key={`${item.id ?? 'att'}-${idx}`}
                                                                    href={href}
                                                                    title={item.name}
                                                                    isImage={isImage}
                                                                    ext={ext}
                                                                    onPreviewImage={(url) => vm.setDescriptionPreviewImage(url)}
                                                                    onOpenFile={(url) => window.open(url, '_blank', 'noopener,noreferrer')}
                                                                />
                                                            ))}
                                                        </AttachmentCardsRow>
                                                    </Container>
                                                ) : null}
                                            </>
                                        )}
                                    </Container>
                                )}

                                {vm.detailTab === 'qa' && (
                                    <Container $display="flex" $flexDirection="column" $gap="10px" style={{ minHeight: '100%' }}>
                                        <ContentCard $padding="14px 16px" style={{ boxShadow: 'none' }}>
                                            <Container $display="flex" $flexDirection="column" $gap="12px" style={{ maxHeight: '100%', overflow: 'auto' }}>
                                                <Container $display="flex" $alignItems="center" $justifyContent="space-between" $gap="10px">
                                                    <PanelSectionLabel style={{ marginBottom: 0 }}>Task submission</PanelSectionLabel>
                                                    <Container $display="flex" $alignItems="center" $gap="10px">
                                                        {vm.qaSubmission?.submittedAt ? (
                                                            <MutedText style={{ margin: 0, fontSize: 12 }}>{vm.qaSubmission.submittedAt}</MutedText>
                                                        ) : null}
                                                        <SubmissionAttachmentsDropdown vm={vm} submission={vm.qaSubmission} />
                                                    </Container>
                                                </Container>

                                                <div>
                                                    <PanelSectionLabel style={{ marginBottom: 6 }}>Notes</PanelSectionLabel>
                                                    {vm.qaSubmission?.notes ? (
                                                        <Markdown
                                                            source={vm.qaSubmission.notes}
                                                            component={CardDescriptionInner}
                                                            richInlineAnchors
                                                            linkPrimary
                                                            emptyFallback="—"
                                                        />
                                                    ) : (
                                                        <MutedText style={{ margin: 0 }}>—</MutedText>
                                                    )}
                                                </div>
                                            </Container>
                                        </ContentCard>
                                    </Container>
                                )}

                                {!hideBugsSection && vm.detailTab === 'bugs' && (
                                    <Container $display="flex" $flexDirection="column" $gap="10px" style={{ minHeight: '100%' }}>
                                        {bugsPanelExtra}
                                        {!bugsPanelExtra &&
                                            !readOnlyBugActions &&
                                            typeof openBugForm === 'function' &&
                                            typeof closeBugForm === 'function' &&
                                            bugForm ? (
                                            <ContentCard $padding="14px 16px">
                                                <Container $display="flex" $alignItems="center" $justifyContent="space-between" $gap="12px">
                                                    <PanelSectionLabel style={{ marginBottom: 0 }}>Create bug</PanelSectionLabel>
                                                    {!showBugForm ? (
                                                        <Button type="button" $primary $blackOnLight $size="13px" $padding="6px 12px" onClick={openBugForm}>
                                                            Report bug
                                                        </Button>
                                                    ) : null}
                                                </Container>

                                                {showBugForm ? (
                                                    <Container $display="flex" $flexDirection="column" $gap="10px" style={{ marginTop: 12 }}>
                                                        <Form form={bugForm} layout="vertical" requiredMark={false} styles={projectSprintTaskModalFormStyles}>
                                                            <Row gutter={[16, 8]}>
                                                                <Col xs={24} sm={12} md={8}>
                                                                    <Form.Item
                                                                        name="severity"
                                                                        initialValue="high"
                                                                        label={<span style={submissionRichDescriptionLabelStyle}>Severity</span>}
                                                                        style={{ marginBottom: 0 }}
                                                                        rules={[{ required: true, message: 'Select severity' }]}
                                                                    >
                                                                        <Select
                                                                            size="large"
                                                                            options={[
                                                                                { value: 'low', label: 'Low' },
                                                                                { value: 'medium', label: 'Medium' },
                                                                                { value: 'high', label: 'High' },
                                                                            ]}
                                                                        />
                                                                    </Form.Item>
                                                                </Col>
                                                            </Row>
                                                            <Form.Item
                                                                name="description"
                                                                style={submissionRichDescriptionFormItemStyle}
                                                                label={<span style={submissionRichDescriptionLabelStyle}>Description</span>}
                                                                rules={[{ required: true, message: 'Add a bug description' }]}
                                                            >
                                                                <RichTextEditor
                                                                    submissionNeutralLight
                                                                    isAttachment
                                                                    isLink
                                                                    isMention
                                                                    placeholder="Describe the bug — bold, lists, @mentions. Attach files or links."
                                                                    showAttachButton
                                                                    mentionUsers={mentionUsers}
                                                                    mentionMilestones={mentionMilestones}
                                                                    mentionSprints={mentionSprints}
                                                                    onFilesSelected={handleBugFilesSelected}
                                                                    onLinkRequest={handleBugLinkRequest}
                                                                    onAttachmentRemoved={(index) => {
                                                                        const p = bugPendingAttachments?.[index];
                                                                        if (p) removeBugAttachment?.(p.id);
                                                                    }}
                                                                    onLinkRemoved={(url) => handleBugEditorLinkRemoved?.(url)}
                                                                    attachmentIndexOffset={Array.isArray(bugPendingAttachments) ? bugPendingAttachments.length : 0}
                                                                />
                                                            </Form.Item>
                                                        </Form>

                                                        {Array.isArray(bugPendingAttachments) && bugPendingAttachments.length > 0 ? (
                                                            <div
                                                                style={{
                                                                    marginTop: 8,
                                                                    padding: '8px 0',
                                                                    borderTop: '1px solid var(--ant-color-border, #f0f0f0)',
                                                                }}
                                                            >
                                                                <PanelSectionLabel
                                                                    style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 6,
                                                                        marginBottom: 8,
                                                                        fontSize: 11,
                                                                        textTransform: 'uppercase',
                                                                        letterSpacing: '0.04em',
                                                                        opacity: 0.75,
                                                                    }}
                                                                >
                                                                    <FileOutlined />
                                                                    Attachments
                                                                </PanelSectionLabel>
                                                                <div
                                                                    style={{
                                                                        display: 'grid',
                                                                        gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                                                                        gap: 10,
                                                                    }}
                                                                >
                                                                    {bugPendingAttachments.map((p) => (
                                                                        <PendingAttachmentTile
                                                                            key={p.id}
                                                                            item={p}
                                                                            onRemove={() => removeBugAttachment?.(p.id)}
                                                                            truncateFileName={truncateFileName}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ) : null}

                                                        {Array.isArray(bugPendingLinks) && bugPendingLinks.length > 0 ? (
                                                            <PendingLinkWrap links={bugPendingLinks} onRemoveLink={(id) => removeBugPendingLink?.(id)} />
                                                        ) : null}

                                                        <Container $display="flex" $justifyContent="flex-end" $alignItems="center" $gap="10px" $flexWrap="wrap" style={{ marginTop: 6 }}>
                                                            <Button
                                                                type="button"
                                                                $primary={false}
                                                                $size="13px"
                                                                $padding="8px 14px"
                                                                onClick={() => closeBugForm?.()}
                                                                disabled={createBugPending}
                                                            >
                                                                Cancel
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                $primary
                                                                $blackOnLight
                                                                $size="13px"
                                                                $padding="8px 14px"
                                                                disabled={createBugPending}
                                                                onClick={() => {
                                                                    bugForm
                                                                        .validateFields()
                                                                        .then(() => saveBugDraft?.())
                                                                        .catch(() => {
                                                                            /* antd surfaces field errors */
                                                                        });
                                                                }}
                                                            >
                                                                Add
                                                            </Button>
                                                        </Container>
                                                    </Container>
                                                ) : null}
                                            </ContentCard>
                                        ) : null}

                                        {!bugsPanelExtra && !readOnlyBugActions && Array.isArray(pendingBugs) && pendingBugs.length ? (
                                            <ContentCard $padding="14px 16px">
                                                <Container $display="flex" $alignItems="center" $justifyContent="space-between" $gap="12px" $flexWrap="wrap" style={{ marginBottom: 10 }}>
                                                    <PanelSectionLabel style={{ marginBottom: 0 }}>
                                                        To submit ({pendingBugs.length})
                                                    </PanelSectionLabel>
                                                    <Button
                                                        type="button"
                                                        $primary
                                                        $blackOnLight
                                                        $size="13px"
                                                        $padding="8px 14px"
                                                        onClick={() => submitAllBugs?.()}
                                                        disabled={createBugPending}
                                                    >
                                                        Submit bugs
                                                    </Button>
                                                </Container>
                                                <Container $display="flex" $flexDirection="column" $gap="10px">
                                                    {pendingBugs.map((d) => (
                                                        <ContentCard key={d.localId} $padding="12px 14px">
                                                            <Container $display="flex" $justifyContent="space-between" $alignItems="center" $gap="10px">
                                                                <Container $display="flex" $flexDirection="column" $gap="2px" style={{ minWidth: 0 }}>
                                                                    <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                        {d.severity ? String(d.severity).toUpperCase() : 'DRAFT'}
                                                                        {d.localId === editingPendingBugId ? ' (editing)' : ''}
                                                                    </div>
                                                                    <MutedText style={{ margin: 0, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                        {String(
                                                                            d.descriptionText || d.descriptionMarkdown || '',
                                                                        ).trim() || '—'}
                                                                    </MutedText>
                                                                </Container>
                                                                <Container $display="flex" $alignItems="center" $gap="8px" style={{ flexShrink: 0 }}>
                                                                    <Button type="button" $primary={false} $size="13px" $padding="6px 10px" onClick={() => startEditPendingBug?.(d.localId)}>
                                                                        Edit
                                                                    </Button>
                                                                    <Button type="button" $primary={false} $size="13px" $padding="6px 10px" onClick={() => removePendingBug?.(d.localId)}>
                                                                        Remove
                                                                    </Button>
                                                                </Container>
                                                            </Container>
                                                        </ContentCard>
                                                    ))}
                                                </Container>
                                            </ContentCard>
                                        ) : null}

                                        {bugsPanelExtra ? null : vm.bugCount === 0 &&
                                            !(Array.isArray(pendingBugs) && pendingBugs.length > 0) ? (
                                            <EmptyStateBlock style={{ minHeight: '100%', padding: '36px 24px' }}>
                                                <EmptyStateIcon>
                                                    <BugOutlined />
                                                </EmptyStateIcon>
                                                <EmptyStateTitle>No bugs reported</EmptyStateTitle>
                                                <EmptyStateDescription>Reported bugs will appear here.</EmptyStateDescription>
                                            </EmptyStateBlock>
                                        ) : (
                                            vm.bugsPresentation.map(({ bug, md, bugHeading, attachments: bugAttachmentItems }, idx) => {
                                                return (
                                                    <ProjectBugCard
                                                        key={bug.id ?? `bug-${idx}`}
                                                        cardStyle={{ boxShadow: 'none' }}
                                                        headerLeft={
                                                            <Container $display="flex" $alignItems="center" $gap="8px" $flexWrap="wrap">
                                                                <SectionBadgeBug>Bug</SectionBadgeBug>
                                                                <span style={{ fontSize: 13, fontWeight: 600 }}>
                                                                    {bugHeading}
                                                                </span>
                                                            </Container>
                                                        }
                                                        headerRight={bug.status != null ? <StatusBadge status={bug.status} size="small" style={{ flexShrink: 0 }} /> : null}
                                                        footer={
                                                            <>
                                                                {bugAttachmentItems.length > 0 ? <PhaseDescriptionAttachments items={bugAttachmentItems} style={{ marginTop: 0 }} /> : null}
                                                                <Button type="button" $primary={false} $size="13px" $padding="4px 10px" icon={<MessageOutlined />} onClick={() => vm.setDiscussionBug(bug)}>
                                                                    Discussion
                                                                </Button>
                                                            </>
                                                        }
                                                    >
                                                        {md ? (
                                                            <ClampedMarkdownDescription
                                                                source={md}
                                                                component={CardDescriptionInner}
                                                                lineClamp={3}
                                                                stepLineClamps={[3, 8, null]}
                                                                resetKey={bug?.id ?? `bug-${idx}`}
                                                                markdownProps={{
                                                                    emptyFallback: '—',
                                                                    linkPrimary: true,
                                                                    richInlineAnchors: true,
                                                                    attachmentItems: bugAttachmentItems,
                                                                    onLinkClick: (payload) => vm.handleMarkdownImageLinkClick(payload, bugAttachmentItems),
                                                                }}
                                                            />
                                                        ) : (
                                                            <MutedText style={{ margin: 0 }}>—</MutedText>
                                                        )}
                                                    </ProjectBugCard>
                                                );
                                            })
                                        )}
                                    </Container>
                                )}

                                {vm.detailTab === 'blockers' && (
                                    <Container $display="flex" $flexDirection="column" $gap="10px" style={{ minHeight: '100%' }}>
                                        {allowAddBlocker ? (
                                            <ActionsWrap style={{ marginBottom: 0, justifyContent: 'flex-end' }}>
                                                <Button type="button" $primary $blackOnLight $size="13px" $padding="8px 16px" onClick={() => { vm.setEditingBlocker(null); vm.setCreateBlockerOpen(true); }}>
                                                    Add blocker
                                                </Button>
                                            </ActionsWrap>
                                        ) : null}
                                        {vm.blockerCount === 0 ? (
                                            <EmptyStateBlock style={{ minHeight: '100%', padding: '36px 24px' }}>
                                                <EmptyStateIcon>
                                                    <StopOutlined />
                                                </EmptyStateIcon>
                                                <EmptyStateTitle>No Blockers</EmptyStateTitle>
                                                <EmptyStateDescription>Add blockers to track what’s preventing progress.</EmptyStateDescription>
                                            </EmptyStateBlock>
                                        ) : (
                                            <Container $display="flex" $flexDirection="column" $gap="10px">
                                                {vm.blockersPresentation.map(
                                                    (
                                                        {
                                                            blocker: b,
                                                            blockerNumber,
                                                            descriptionSource,
                                                            attachmentItems,
                                                            attachmentsCount,
                                                            createdAtLabel,
                                                            createdByName,
                                                            createdByRoleLabel,
                                                            createdByInitials,
                                                            createdByColor,
                                                            canEdit,
                                                            canDelete,
                                                        },
                                                        idx,
                                                    ) => (
                                                        <ContentCard
                                                            key={b?.id ?? `task-blocker-${idx}`}
                                                            $padding="14px 16px"
                                                            style={vm.getBlockerCardStyle(b)}
                                                            onMouseEnter={() => vm.setHoveredBlockerId(b?.id ?? b?._id ?? null)}
                                                            onMouseLeave={() => vm.setHoveredBlockerId(null)}
                                                            onClick={() => vm.setDiscussionBlocker(b)}
                                                        >
                                                            <Container $display="flex" $justifyContent="space-between" $gap="12px">
                                                                <Container $display="flex" $flexDirection="column" $gap="8px" style={{ minWidth: 0, flex: 1 }}>
                                                                    <Container $display="flex" $alignItems="center" $gap="10px" style={{ minWidth: 0 }}>
                                                                        <span style={{ width: 34, height: 34, borderRadius: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,77,79,0.10)', color: '#ff4d4f', flexShrink: 0 }}>
                                                                            <StopOutlined />
                                                                        </span>
                                                                        <span style={{ fontSize: 13, fontWeight: 800, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                            {String(blockerNumber || `Blocker ${idx + 1}`)}
                                                                        </span>
                                                                    </Container>

                                                                    <Markdown
                                                                        source={descriptionSource}
                                                                        component="div"
                                                                        variant="compact"
                                                                        lineClamp={5}
                                                                        richInlineAnchors
                                                                        linkPrimary
                                                                        attachmentItems={attachmentItems}
                                                                        onLinkClick={(payload) => vm.handleMarkdownImageLinkClick(payload, attachmentItems)}
                                                                        emptyFallback="—"
                                                                    />

                                                                    <Container $display="flex" $alignItems="center" $justifyContent="space-between" $gap="10px">
                                                                        <Container $display="flex" $alignItems="center" $gap="10px">
                                                                            <Container
                                                                                $display="inline-flex"
                                                                                $alignItems="center"
                                                                                $gap="6px"
                                                                                style={{
                                                                                    padding: '4px 10px',
                                                                                    borderRadius: 999,
                                                                                    border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                                                                                    background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                                                                                    fontSize: 12,
                                                                                    fontWeight: 600,
                                                                                }}
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                }}
                                                                            >
                                                                                <PaperClipOutlined style={{ fontSize: 12 }} />
                                                                                <span>{attachmentsCount || 0}</span>
                                                                            </Container>

                                                                            <IconChipButton
                                                                                ariaLabel="Edit blocker"
                                                                                disabled={!canEdit}
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                    if (!canEdit) return;
                                                                                    vm.setEditingBlocker(b);
                                                                                    vm.setCreateBlockerOpen(true);
                                                                                }}
                                                                            >
                                                                                <EditOutlined style={{ fontSize: 14 }} />
                                                                            </IconChipButton>
                                                                            <IconChipButton
                                                                                ariaLabel="Delete blocker"
                                                                                disabled={!canDelete}
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                    if (!canDelete) return;
                                                                                    vm.handleDeleteBlocker(b);
                                                                                }}
                                                                            >
                                                                                <DeleteOutlined style={{ fontSize: 14 }} />
                                                                            </IconChipButton>
                                                                        </Container>

                                                                        <Container $display="flex" $alignItems="center" $gap="10px" style={{ flexShrink: 0 }}>
                                                                            {createdAtLabel ? (
                                                                                <MutedText style={{ margin: 0, fontSize: 12, whiteSpace: 'nowrap' }}>
                                                                                    {createdAtLabel}
                                                                                </MutedText>
                                                                            ) : null}
                                                                            <div
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                }}
                                                                            >
                                                                                <UserAvatarWithRoleTooltip
                                                                                    name={createdByName || 'User'}
                                                                                    roleLabel={createdByRoleLabel}
                                                                                    initials={createdByInitials}
                                                                                    color={createdByColor}
                                                                                    size={44}
                                                                                    fontSize={14}
                                                                                />
                                                                            </div>
                                                                        </Container>
                                                                    </Container>
                                                                </Container>

                                                            </Container>
                                                        </ContentCard>
                                                    ),
                                                )}
                                            </Container>
                                        )}
                                    </Container>
                                )}

                                {vm.detailTab === 'consultation' && vm.consultationData && (
                                    <Container $display="flex" $flexDirection="column" $gap="10px" style={{ minHeight: '100%' }}>
                                        <ContentCard $padding="14px 16px" style={{ boxShadow: 'none' }}>
                                            <Container $display="flex" $flexDirection="column" $gap="12px">
                                                {/* Header row */}
                                                <Container $display="flex" $alignItems="center" $justifyContent="space-between" $gap="10px">
                                                    <PanelSectionLabel style={{ marginBottom: 0 }}>Consultation</PanelSectionLabel>
                                                    <Container $display="flex" $alignItems="center" $gap="8px">
                                                        {/* QA Passed chip */}
                                                        <span
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: 5,
                                                                padding: '4px 10px',
                                                                borderRadius: 999,
                                                                fontSize: 12,
                                                                fontWeight: 600,
                                                                border: '1px solid',
                                                                borderColor: vm.consultationData.qaPassed ? 'rgba(82,196,26,0.4)' : 'rgba(255,77,79,0.3)',
                                                                background: vm.consultationData.qaPassed ? 'rgba(82,196,26,0.08)' : 'rgba(255,77,79,0.06)',
                                                                color: vm.consultationData.qaPassed ? '#389e0d' : '#cf1322',
                                                            }}
                                                        >
                                                            {vm.consultationData.qaPassed
                                                                ? <CheckCircleOutlined style={{ fontSize: 12 }} />
                                                                : <CloseCircleOutlined style={{ fontSize: 12 }} />}
                                                            QA {vm.consultationData.qaPassed ? 'Passed' : 'Pending'}
                                                        </span>
                                                        {/* Attachments count */}
                                                        <span
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: 5,
                                                                padding: '4px 10px',
                                                                borderRadius: 999,
                                                                fontSize: 12,
                                                                fontWeight: 600,
                                                                border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                                                                background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                                                            }}
                                                        >
                                                            <PaperClipOutlined style={{ fontSize: 12 }} />
                                                            {vm.consultationData.attachmentCount}
                                                        </span>
                                                        {/* Consultant count */}
                                                        {vm.consultationData.consultantIds.length > 0 ? (
                                                            <span
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: 5,
                                                                    padding: '4px 10px',
                                                                    borderRadius: 999,
                                                                    fontSize: 12,
                                                                    fontWeight: 600,
                                                                    border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                                                                    background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                                                                }}
                                                            >
                                                                <TeamOutlined style={{ fontSize: 12 }} />
                                                                {vm.consultationData.consultantIds.length}
                                                            </span>
                                                        ) : null}
                                                    </Container>
                                                </Container>

                                                {/* Description */}
                                                <div>
                                                    <PanelSectionLabel style={{ marginBottom: 6 }}>Notes</PanelSectionLabel>
                                                    {vm.consultationData.description ? (
                                                        <Markdown
                                                            source={vm.consultationData.description}
                                                            component={CardDescriptionInner}
                                                            richInlineAnchors
                                                            linkPrimary
                                                            emptyFallback="—"
                                                        />
                                                    ) : (
                                                        <MutedText style={{ margin: 0 }}>—</MutedText>
                                                    )}
                                                </div>
                                            </Container>
                                        </ContentCard>
                                    </Container>
                                )}

                                {vm.showActivityTab && vm.detailTab === 'activity' && (
                                    <Container $display="flex" $flexDirection="column" $gap="10px" style={{ minHeight: '100%' }}>
                                        {vm.activityCount === 0 ? (
                                            <EmptyStateBlock style={{ minHeight: '100%', padding: '36px 24px' }}>
                                                <EmptyStateIcon>
                                                    <MessageOutlined />
                                                </EmptyStateIcon>
                                                <EmptyStateTitle>No activity log</EmptyStateTitle>
                                                <EmptyStateDescription>Task activity will appear here.</EmptyStateDescription>
                                            </EmptyStateBlock>
                                        ) : (
                                            <TimelineWrap style={{ padding: 0 }}>
                                                <TimelineLine />
                                                {vm.activityPresentation.map(({ log, msg, when }, idx) => {
                                                    const side = idx % 2 === 0 ? 'left' : 'right';
                                                    return (
                                                        <TimelineItem key={log?.id ?? `log-${idx}`}>
                                                            <TimelineIconWrap>
                                                                <TimelineIcon $color="var(--ant-color-primary, #7c3aed)">
                                                                    <MessageOutlined />
                                                                </TimelineIcon>
                                                            </TimelineIconWrap>
                                                            <TimelineItemContent $side={side}>
                                                                <TimelineDate $side={side}>{when}</TimelineDate>
                                                                <TimelineCard $large $interactive={false}>
                                                                    <TimelineCardTitle>{msg}</TimelineCardTitle>
                                                                    <TimelineCardMeta>
                                                                        <MessageOutlined style={{ fontSize: 12 }} />
                                                                        Activity
                                                                    </TimelineCardMeta>
                                                                </TimelineCard>
                                                            </TimelineItemContent>
                                                        </TimelineItem>
                                                    );
                                                })}
                                            </TimelineWrap>
                                        )}
                                    </Container>
                                )}
                            </div>
                        </Container>
                    </CreateRoleModalBody>
                ) : null}
            </AppModal>

            <BugDiscussionSidePanel projectId={projectId} bug={vm.discussionBug} mentionUsers={mentionUsers} onClose={() => vm.setDiscussionBug(null)} />
            <BlockerDiscussionSidePanel projectId={projectId} taskId={vm.taskId} blocker={vm.discussionBlocker} onClose={() => vm.setDiscussionBlocker(null)} mentionUsers={mentionUsers} />

            <TaskBlockerCreateModal
                projectId={projectId}
                taskId={vm.taskId}
                open={vm.createBlockerOpen}
                onCancel={() => vm.setCreateBlockerOpen(false)}
                onCreated={() => onRequestRefresh?.()}
                blocker={vm.editingBlocker}
                mentionUsers={mentionUsers}
                mentionMilestones={mentionMilestones}
                mentionSprints={mentionSprints}
            />

            {typeof bugLinkModalOpen === 'boolean' ? (
                <MilestoneLinkModal
                    open={bugLinkModalOpen}
                    onCancel={() => closeBugLinkModal?.()}
                    linkModalForm={bugLinkForm}
                    handleLinkModalSubmit={(payload) => handleBugLinkModalSubmit?.(payload)}
                />
            ) : null}

            {vm.descriptionPreviewImage ? (
                <Image
                    src={vm.descriptionPreviewImage}
                    alt="Preview"
                    style={{ display: 'none' }}
                    preview={{
                        visible: true,
                        src: vm.descriptionPreviewImage,
                        zIndex: 2000,
                        rootClassName: 'global-image-preview-root',
                        onVisibleChange: (visible) => {
                            if (!visible) vm.setDescriptionPreviewImage('');
                        },
                    }}
                />
            ) : null}
        </>
    );
}

